import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExcelUpload = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const t = (en, mr) => language === 'mr' ? mr : en;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setResult(null);
      } else {
        toast.error(t('Please select an Excel file (.xlsx or .xls)', 'कृपया Excel फाइल निवडा (.xlsx किंवा .xls)'));
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error(t('Please select a file first', 'कृपया प्रथम फाइल निवडा'));
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setResult({
        success: true,
        message: response.data.message,
        partywise: response.data.partywise_voting_count,
        seats: response.data.seats_won_count
      });

      toast.success(t('Data uploaded successfully!', 'डेटा यशस्वीरित्या अपलोड झाला!'));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      setResult({
        success: false,
        message: errorMsg
      });
      toast.error(t('Upload failed: ', 'अपलोड अयशस्वी: ') + errorMsg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="excel-upload-page">
      <div>
        <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('Excel Data Upload', 'Excel डेटा अपलोड')}
        </h1>
        <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('Upload election data from Excel file', 'Excel फाइलमधून निवडणूक डेटा अपलोड करा')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              {t('Upload Excel File', 'Excel फाइल अपलोड करा')}
            </CardTitle>
            <CardDescription>
              {t('Select your election result Excel file to update the database', 
                 'डेटाबेस अपडेट करण्यासाठी तुमची निवडणूक निकाल Excel फाइल निवडा')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="excel-file"
                data-testid="excel-file-input"
              />
              <label htmlFor="excel-file" className="cursor-pointer">
                <p className="text-lg font-medium text-gray-700">
                  {file ? file.name : t('Click to select Excel file', 'Excel फाइल निवडण्यासाठी क्लिक करा')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t('Supports .xlsx and .xls files', '.xlsx आणि .xls फाइल्स समर्थित')}
                </p>
              </label>
            </div>

            {file && (
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">{file.name}</span>
                </div>
                <span className="text-sm text-green-600">
                  {(file.size / 1024).toFixed(1)} KB
                </span>
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full"
              size="lg"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('Uploading...', 'अपलोड होत आहे...')}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('Upload & Update Data', 'अपलोड करा आणि डेटा अपडेट करा')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Instructions Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-500" />
              {t('Excel File Format', 'Excel फाइल फॉर्मेट')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700">
                {t('Required Sheets:', 'आवश्यक शीट्स:')}
              </h4>
              
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="font-medium text-blue-700">1. POLLITICAL PARTY VOTES POLL</p>
                <p className="text-sm text-blue-600">
                  {t('Statement 8: Party-wise voting details', 'विधान 8: पक्षनिहाय मतदान तपशील')}
                </p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="font-medium text-green-700">2. POLLITICAL PARTY SEATS WON</p>
                <p className="text-sm text-green-600">
                  {t('Statement 9: Seats won by parties', 'विधान 9: पक्षांनी जिंकलेल्या जागा')}
                </p>
              </div>

              <div className="p-3 bg-yellow-50 rounded-lg mt-4">
                <p className="font-medium text-yellow-700">
                  {t('⚠️ Important:', '⚠️ महत्त्वाचे:')}
                </p>
                <p className="text-sm text-yellow-600">
                  {t('Uploading will replace all existing data in Party-wise Voting and Seats Won tables.', 
                     'अपलोड केल्यास पक्षनिहाय मतदान आणि जिंकलेल्या जागा टेबलमधील सर्व विद्यमान डेटा बदलला जाईल.')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Result Card */}
      {result && (
        <Card className={`shadow-lg ${result.success ? 'border-green-500' : 'border-red-500'} border-2`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              {result.success ? (
                <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                  {result.success 
                    ? t('Upload Successful!', 'अपलोड यशस्वी!') 
                    : t('Upload Failed', 'अपलोड अयशस्वी')}
                </h3>
                <p className="text-gray-600 mt-1">{result.message}</p>
                
                {result.success && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{result.partywise}</p>
                      <p className="text-sm text-blue-500">
                        {t('Party-wise Voting Records', 'पक्षनिहाय मतदान रेकॉर्ड')}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{result.seats}</p>
                      <p className="text-sm text-green-500">
                        {t('Seats Won Records', 'जिंकलेल्या जागा रेकॉर्ड')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExcelUpload;
